// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import { LockableTextField } from '@components/Card2/LockableTextField';
import { cardEditContext } from '@context/card-edit-context';
import { PopoverContext } from '@context/popover-context';
import useNode from '@hooks/use-node';
import {
  autocorrectURLValue,
  getDefaultUrlValueLabel,
  getSuggestionsByFieldType,
  getSuggestionsFromURLFieldValue,
  isCompleteURL,
  isPartialURL,
  isURLFieldValueValid,
} from '@modules/card-helpers';
import { Box, Stack } from '@ui-lib';
import { AnimatePresence } from 'motion/react';
import { type FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CardUrlType } from 'types';

import { LabelField } from '../../LabelField';
import { URLPreview } from '../UrlPreview/UrlPreview';
import { BasicUrlEditorContainer, UrlPreviewContainer } from './BasicUrlEditor.css';

function useFocusMainInputOnShow(inputNode: HTMLInputElement | undefined) {
  const { show } = useContext(PopoverContext);
  useEffect(() => {
    if (show && inputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            inputNode.focus();
          }, 300);
          observer.unobserve(entry.target);
        }
      });
      intersectionObserver.observe(inputNode);
    }
  }, [show, inputNode]);
}

interface BasicUrlProps {
  shorthand: string;
  value: string;
  valueLocked: boolean;
  valueLockEnabled?: boolean;
  setValueLocked: (locked: boolean) => void;
  // valueRequired: boolean;
  label: string;
  labelLocked: boolean;
  labelLockEnabled?: boolean;
  setLabelLocked: (locked: boolean) => void;
  // labelRequired: boolean;
  // serviceUrl: string;
  setShorthand: (value: string) => void;
  setValue: (value: string) => void;
  setLabel: (value: string) => void;
  setType: (value: string) => void;
  type: string;
}

export function BasicUrlEditor({
  shorthand,
  value,
  labelLocked,
  valueLocked,
  valueLockEnabled,
  setValueLocked,
  // valueRequired,
  label,
  labelLockEnabled,
  setLabelLocked,
  // labelRequired,
  type,
  setShorthand,
  setLabel,
}: BasicUrlProps): React.ReactElement {
  const { editPageMode } = useContext(cardEditContext);
  const [showURLPreview, setShowURLPreview] = useState(editPageMode === 'update');
  const [valueInputNode, valueInputRef] = useNode<HTMLInputElement>();

  useFocusMainInputOnShow(valueInputNode);

  const fieldNeedsValidUrl =
    type === 'calendar' ||
    type === 'company_url' ||
    type === 'link' ||
    type === 'linkedin' ||
    type === 'threads';

  const isValidUrl = useMemo(() => {
    if (type === 'linkedin') {
      return isURLFieldValueValid('linkedin', shorthand);
    }

    return isPartialURL(shorthand) || isCompleteURL(shorthand);
  }, [shorthand, type]);
  const invalidUrlMessage =
    type === 'linkedin' ? 'Enter your LinkedIn profile URL or username' : 'Enter a valid URL';

  const suggestions = useMemo(() => {
    const derivedSuggestions = getSuggestionsFromURLFieldValue(type as CardUrlType, value);
    const baseSuggestions = getSuggestionsByFieldType('url')(type as CardUrlType);
    return [...baseSuggestions, ...derivedSuggestions];
  }, [type, value]);

  const handleValueChange = (event: FormEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    const correctedValue: string = autocorrectURLValue(newValue, type as CardUrlType);
    setShorthand(correctedValue);
  };

  const handleInputBlur = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  const handleInputPaste = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  return (
    <Box>
      <Stack gap="large">
        {/* <TypeSelector type={type} onSelect={handleTypeSelect} /> */}
        <Box className={BasicUrlEditorContainer}>
          <LockableTextField
            ref={valueInputRef}
            value={shorthand}
            label={getDefaultUrlValueLabel(type as CardUrlType)}
            onChange={handleValueChange}
            onBlur={handleInputBlur}
            onPaste={handleInputPaste}
            locked={valueLocked}
            toggleLocked={valueLockEnabled ? () => setValueLocked(!valueLocked) : undefined}
            required
            tone={fieldNeedsValidUrl && !isValidUrl ? 'caution' : 'neutral'}
            message={fieldNeedsValidUrl && !isValidUrl ? invalidUrlMessage : ''}
          />

          {value && isValidUrl && showURLPreview && (
            <Box className={UrlPreviewContainer}>
              <AnimatePresence>
                <URLPreview value={value} type={type} />
              </AnimatePresence>
            </Box>
          )}
        </Box>

        <Stack gap="medium">
          <LabelField
            label={label}
            subject="title"
            labelLocked={labelLocked}
            labelSuggestions={suggestions}
            setLabel={setLabel}
            setLabelLocked={labelLockEnabled ? setLabelLocked : undefined}
            required
          />
        </Stack>
      </Stack>
    </Box>
  );
}
